import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductCard from '../components/ProductCard';
import supabase from '../supabase/supabase';
import { assets } from '../data/catalog';

const { FiSearch, FiGrid, FiList, FiSliders, FiX, FiRotateCcw } = FiIcons;

function ShopFilters({
  categories,
  open,
  close,
  selectedCategory,
  setSelectedCategory,
  stock,
  setStock,
  priceLimit,
  setPriceLimit
}) {
  return (
    <aside className={`shop-filters ${open ? 'filters-open' : ''}`}>
      <button className="filter-close" onClick={close}><SafeIcon icon={FiX} /></button>
      <h3>Categories</h3>
      {categories.map((item) => (
        <label key={item.id}>
          <input
            type="checkbox"
            checked={selectedCategory === item.name}
            onChange={() => setSelectedCategory(selectedCategory === item.name ? '' : item.name)}
          /> {item.name}
        </label>
      ))}
      <h3>Price Range</h3>
      <input
        type="range"
        min="500"
        max="3000"
        step="50"
        value={priceLimit}
        onChange={(event) => setPriceLimit(Number(event.target.value))}
        aria-label={`Maximum price Rs. ${priceLimit.toLocaleString()}`}
      />
      <div className="range-labels">
        <span>Rs. 500</span>
        <span>Up to Rs. {priceLimit.toLocaleString()}</span>
      </div>
      <h3>Availability</h3>
      <label><input type="radio" name="stock" checked={stock === 'all'} onChange={() => setStock('all')} /> All</label>
      <label><input type="radio" name="stock" checked={stock === 'in'} onChange={() => setStock('in')} /> In Stock</label>
      <label><input type="radio" name="stock" checked={stock === 'out'} onChange={() => setStock('out')} /> Out of Stock</label>
    </aside>
  );
}

function ShopPage({ onAdd }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInput = useRef(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [stock, setStock] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceLimit, setPriceLimit] = useState(3000);
  const [list, setList] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dbProducts, setDbProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelectedCategory(searchParams.get('category') || '');
    if (searchParams.get('focus') === 'search') {
      window.setTimeout(() => searchInput.current?.focus(), 0);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('products_1750000001')
        .select('*')
        .order('id');

      if (!error && data && data.length > 0) {
        setDbProducts(data);
      } else {
        setDbProducts([]);
      }

      const { data: categoryData } = await supabase
        .from('product_categories_1750000004')
        .select('*')
        .eq('active', true)
        .order('sort_order');

      setCategories(categoryData || []);
      setLoading(false);
    };

    loadProducts();
  }, []);

  const hasActiveFilters = query !== '' || sort !== 'newest' || stock !== 'all' || selectedCategory !== '' || priceLimit !== 3000;

  const updateCategory = (category) => {
    const nextParams = new URLSearchParams(searchParams);

    if (category) {
      nextParams.set('category', category);
    } else {
      nextParams.delete('category');
    }

    setSearchParams(nextParams);
  };

  const resetFilters = () => {
    setQuery('');
    setSort('newest');
    setStock('all');
    setPriceLimit(3000);
    updateCategory('');
  };

  const products = useMemo(() => {
    let result = dbProducts.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
    if (selectedCategory) result = result.filter((item) => item.category === selectedCategory);
    result = result.filter((item) => Number(item.price) <= priceLimit);
    if (stock === 'in') result = result.filter((item) => item.available);
    if (stock === 'out') result = result.filter((item) => !item.available);
    if (sort === 'low') result.sort((a, b) => Number(a.price) - Number(b.price));
    if (sort === 'high') result.sort((a, b) => Number(b.price) - Number(a.price));
    return result;
  }, [dbProducts, query, sort, stock, selectedCategory, priceLimit]);

  return (
    <main className="shop-page">
      <section className="shop-hero section-shell">
        <img src={assets.signature} alt="Botanical collection" />
        <div><span className="eyebrow">Darshana collection</span><h1>Shop All Botanical Products</h1>
          <p>Premium dried flowers, herbal teas and natural wellness ingredients.</p></div>
      </section>
      <section className="shop-toolbar section-shell">
        <div className="search-field"><SafeIcon icon={FiSearch} /><input ref={searchInput} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products" /></div>
        <span className="product-counter">Showing {products.length} of {dbProducts.length} Products</span>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort products">
          <option value="newest">Newest</option><option value="selling">Best Selling</option>
          <option value="low">Price Low–High</option><option value="high">Price High–Low</option>
        </select>
        {hasActiveFilters && (
          <button className="reset-filters-btn" onClick={resetFilters} title="Reset all filters">
            <SafeIcon icon={FiRotateCcw} />
            <span>Reset</span>
          </button>
        )}
        <div className="view-toggle">
          <button className={!list ? 'active' : ''} onClick={() => setList(false)}><SafeIcon icon={FiGrid} /></button>
          <button className={list ? 'active' : ''} onClick={() => setList(true)}><SafeIcon icon={FiList} /></button>
        </div>
        <button className="mobile-filter-button" onClick={() => setFiltersOpen(true)}><SafeIcon icon={FiSliders} /> Filters</button>
      </section>
      <section className="shop-layout section-shell">
        <ShopFilters
          categories={categories}
          open={filtersOpen}
          close={() => setFiltersOpen(false)}
          selectedCategory={selectedCategory}
          setSelectedCategory={updateCategory}
          stock={stock}
          setStock={setStock}
          priceLimit={priceLimit}
          setPriceLimit={setPriceLimit}
        />
        <div className={`shop-products ${list ? 'list-products' : ''}`}>
          {loading ? (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0', color: '#6e716e' }}>Loading products…</p>
          ) : products.length === 0 ? (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0', color: '#6e716e' }}>No products found.</p>
          ) : (
            products.map((product) => <ProductCard key={product.id} product={product} onAdd={onAdd} />)
          )}
        </div>
      </section>
    </main>
  );
}

export default ShopPage;